import { CYCLE_BUCKET_LABELS, formatUsdCompact, formatUsdFull } from "@po/shared";
import type { Overview } from "../lib/api";
import { int, MONEY_CAPTION, stageLabel, stageVar } from "../lib/format";
import { useDashboard } from "../pages/Dashboard";
import { ChartCard, Columns, GroupedColumns, HBars } from "../components/charts";
import { monthLabel, useTabQuery } from "./common";

const FUNNEL = ["draft", "awaiting_superintendent", "sourcing", "ready_to_order", "po_in_approval", "ordered", "partly_received", "received"];
const CYCLE_COLORS = ["var(--age-0)", "var(--age-0)", "var(--age-1)", "var(--age-2)", "var(--age-3)"];

/** First and last day of a "YYYY-MM" month. */
function monthRange(ym: string) {
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, "0")}` };
}

/**
 * Where requisitions are stuck: one row per stage with its count and a bar scaled to the largest
 * stage. A row click toggles the stage filter, like a chart bar (SPEC §6.3).
 */
function StageTable({ rows, selected, onSelect }: { rows: { key: string; label: string; value: number; color: string }[]; selected: string[]; onSelect: (k: string) => void }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <section className="card p-4 md:p-5" aria-label="Where requisitions are stuck">
      <h3 className="text-[15px] font-bold mb-3">Where requisitions are stuck</h3>
      <div className="grid grid-cols-[minmax(110px,180px)_72px_1fr] items-center gap-x-4 text-[10.5px] font-bold uppercase tracking-[0.05em] text-ink2 pb-1.5">
        <span>Stage</span>
        <span className="text-right">Count</span>
        <span>Volume</span>
      </div>
      <ul>
        {rows.map((r) => {
          const on = selected.includes(r.key);
          return (
            <li key={r.key}>
              <button
                onClick={() => onSelect(r.key)}
                aria-pressed={on}
                className={`w-full grid grid-cols-[minmax(110px,180px)_72px_1fr] items-center gap-x-4 py-[7px] text-left rounded hover:bg-hover ${selected.length && !on ? "opacity-40" : ""}`}
              >
                <span className="text-[13px] font-semibold truncate">{r.label}</span>
                <span className="text-[13px] text-right tnum">{int(r.value)}</span>
                <span className="h-2 rounded-full bg-[var(--line)] overflow-hidden">
                  <span className="block h-full rounded-full" style={{ width: `${(r.value / max) * 100}%`, minWidth: r.value ? 4 : 0, background: r.color }} />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] text-muted mt-2">Fully received counts those received within the date range; the other stages count every open requisition.</p>
    </section>
  );
}

export function OverviewTab() {
  const { filters, toggle, setSearch } = useDashboard();
  const { data: o } = useTabQuery<Overview>("/overview");
  if (!o) return <div className="card h-64 animate-pulse" />;
  const funnel = FUNNEL.map((s) => ({ key: s, label: stageLabel(s), value: o.funnel.find((f) => f.stage === s)?.count ?? 0, color: stageVar(s) }));
  const c = o.cycle;
  const targetBucket = c.target == null ? null : c.target <= 15 ? 0 : c.target <= 30 ? 1 : c.target <= 45 ? 2 : c.target <= 60 ? 3 : 4;
  const versus = c.target != null ? `target ${c.target} d` : c.pctWithinTarget != null ? `${c.pctWithinTarget} % within target` : null;
  return (
    <div className="space-y-4">
      <StageTable rows={funnel} selected={filters.stage ?? []} onSelect={(k) => toggle("stage", k)} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title={`Cycle time${c.median == null ? "" : ` · ${Math.round(c.median)} d median`}`}
          caption={`Requested to fully received, in the date range${versus ? ` · ${versus}` : ""}`}
          table={{ head: ["Days", "Requisitions"], rows: CYCLE_BUCKET_LABELS.map((l, i) => [l, c.buckets[i] ?? 0]) }}
        >
          <Columns
            data={CYCLE_BUCKET_LABELS.map((l, i) => ({ key: String(i), label: l, value: c.buckets[i] ?? 0, color: CYCLE_COLORS[i]! }))}
            selected={(filters.cycle ?? []).map(String)}
            onSelect={(k) => toggle("cycle", Number(k))}
            target={targetBucket == null ? undefined : { x: CYCLE_BUCKET_LABELS[targetBucket]!, label: `target ${c.target} d` }}
            height={190}
          />
        </ChartCard>
        <ChartCard title="PO pending receipt by vessel" caption={MONEY_CAPTION} table={{ head: ["Vessel", "USD"], rows: o.commitmentsByVessel.map((v) => [v.name, formatUsdFull(v.usd)]) }}>
          <HBars
            data={o.commitmentsByVessel.map((v) => ({ key: v.name, label: v.name, value: v.usd, color: "var(--stage-ordered)" }))}
            selected={filters.vessel ?? []}
            onSelect={(k) => toggle("vessel", k)}
            format={formatUsdCompact}
          />
        </ChartCard>
      </div>
      <ChartCard
        title="Monthly throughput"
        caption="POs placed (order date) and requisitions fully received"
        table={{ head: ["Month", "POs placed", "Requisitions received"], rows: o.throughput.map((m) => [monthLabel(m.month), m.posPlaced, m.reqsReceived]) }}
      >
        <GroupedColumns
          onSelect={(i) => {
            // A month click narrows the date range to that month; a second click restores it.
            const r = monthRange(o.throughput[i]!.month);
            const same = filters.from === r.from && filters.to === r.to;
            setSearch(same ? { from: undefined, to: undefined } : r);
          }}
          data={o.throughput.map((m) => ({ label: monthLabel(m.month), posPlaced: m.posPlaced, reqsReceived: m.reqsReceived }))}
          series={[
            { key: "posPlaced", label: "POs placed", color: "var(--stage-ordered)" },
            { key: "reqsReceived", label: "Requisitions received", color: "var(--stage-received)" },
          ]}
        />
      </ChartCard>
    </div>
  );
}
